// Copyright 1999-2017. Plesk International GmbH. All rights reserved.

import { createElement, Component, cloneElement, isValidElement, isRtl } from '../../utils';
import { wrapFunction } from '../utils';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { CLS_PREFIX, Z_INDEX_DROPDOWN } from '../../constants';
import Button from '../Button';
import AutoClosable from '../AutoClosable';
import Popper from '../Popper';
import Translate from '../Translate';

import '../../helpers/base.less';
import '../Menu/Menu.less';
import './Dropdown.less';

/**
 * `Dropdown` component is a [Button](#!/Button) that opens a drop-down (nested) [Menu](#!/Menu).
 * Use dropdown for grouping actions that are not used often or are less important than others -- in other words,
 * for stuff that you don't want to show users all the time, especially if you have limited screen space.
 */
class Dropdown extends Component {
    state = {
        opened: false,
    };

    handleOpen = () => {
        this.setState({
            opened: true,
        });
    };

    handleClose = () => {
        setTimeout(() => {
            this.setState({
                opened: false,
            });
        }, 0);
    };

    computePlacement(placement) {
        const [side, align] = placement.split('-');

        if (align && isRtl() && ['top', 'bottom'].indexOf(side) !== -1) {
            return `${side}-${align === 'start' ? 'end' : 'start'}`;
        }

        return placement;
    }

    render() {
        const { baseClassName, className, children, menu, menuPlacement, ...props } = this.props;
        const { opened } = this.state;
        let target = null;
        const clonedMenu = cloneElement(menu, {
            onItemClick: wrapFunction(menu.props.onItemClick, this.handleClose),
            className: classNames(`${baseClassName}__menu`, menu.props.className),
        });

        if (!children || typeof children === 'string' || (isValidElement(children) && children.type === Translate)) {
            target = <Button caret onClick={this.handleOpen}>{children}</Button>;
        } else {
            target = cloneElement(children, {
                onClick: this.handleOpen,
            });
        }

        return (
            <div
                className={classNames(
                    baseClassName,
                    {
                        [`${baseClassName}--opened`]: opened,
                    },
                    className,
                )}
                {...props}
            >
                <Popper
                    target={target}
                    placement={this.computePlacement(menuPlacement)}
                    show={opened}
                    className={`${baseClassName}__popper`}
                    zIndex={Z_INDEX_DROPDOWN}
                >
                    <AutoClosable onClose={this.handleClose}>{clonedMenu}</AutoClosable>
                </Popper>
            </div>
        );
    }
}

Dropdown.propTypes = {
    /**
     * Dropdown [menu](#!/Menu).
     */
    menu: PropTypes.element.isRequired,
    /**
     * Menu placement.
     */
    menuPlacement: PropTypes.string,
    /**
     * Content of the `Dropdown`.
     */
    children: PropTypes.node,
    /**
     * @ignore
     */
    className: PropTypes.string,
    /**
     * @ignore
     */
    baseClassName: PropTypes.string,
};

Dropdown.defaultProps = {
    menuPlacement: 'bottom-start',
    children: null,
    className: null,
    baseClassName: `${CLS_PREFIX}dropdown`,
};

export default Dropdown;



// WEBPACK FOOTER //
// ./components/Dropdown/Dropdown.js